import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { NuqsAdapter } from "nuqs/adapters/react-router";
import { Camera, Palette, ShoppingCart, Video } from "lucide-react";
import { useAuthStore } from "@groot/shell/store/auth";
import { ProtectedRoute } from "@groot/shell/components/ProtectedRoute";
import { ShellProvider, type ShellConfig } from "@groot/shell/lib/shell-config";
import { Storage } from "@groot/shell/pages/storage/Storage";
import { Login } from "@groot/shell/pages/auth/Login";
import { Jobs, JobDetail } from "@groot/jobs/client";
import { GrootUIProvider } from "@groot/ui";
import { AppLayout } from "./components/AppLayout";
import { Dashboard } from "./pages/dashboard/Dashboard";
import { Settings } from "@groot/shell/pages/settings/Settings";
import { Customers } from "./pages/customer/Customers";
import { CustomerDetail } from "./pages/customer/CustomerDetail";
import { Products } from "./pages/product/Products";
import { Orders } from "./pages/order/Orders";
import { OrderDetail } from "./pages/order/OrderDetail";
import { OrderInvoice } from "./pages/order/OrderInvoice";
import { PublicOrderDetail } from "./pages/order/PublicOrderDetail";
import { PhotoRequests } from "./pages/photo-request/PhotoRequests";
import { PhotoRequestDetail } from "./pages/photo-request/PhotoRequestDetail";
import { PublicPhotoUpload } from "./pages/photo-request/PublicPhotoUpload";
import { MomentRequests } from "./pages/moment-request/MomentRequests";
import { MomentRequestDetail } from "./pages/moment-request/MomentRequestDetail";
import { PublicMomentUpload } from "./pages/moment-request/PublicMomentUpload";
import { PublicMomentView } from "./pages/moment-request/PublicMomentView";
import { PrintBatches } from "./pages/print-batch/PrintBatches";
import { LoadingState } from "@groot/ui/loading-spinner";

// Canvas (Konva) and gesture (motion) code only loads on these routes.
const PrintBatchEditor = lazy(() =>
  import("./pages/print-batch/PrintBatchEditor").then((m) => ({ default: m.PrintBatchEditor })),
);
const PublicPrintPreview = lazy(() =>
  import("./pages/print-preview/PublicPrintPreview").then((m) => ({
    default: m.PublicPrintPreview,
  })),
);

const SHELL_CONFIG: ShellConfig = {
  brand: { label: "Riqah Design", icon: Palette, to: "/" },
  login: {
    tagline: "Backstage for Riqah Design.",
    description:
      "Orders, customers, photo requests and moments: everything behind the studio in one place.",
    features: [
      { icon: ShoppingCart, title: "Orders", desc: "Track orders from new to delivered." },
      { icon: Camera, title: "Photo requests", desc: "Collect customer photos with a link." },
      { icon: Video, title: "Moments", desc: "Share video moments with a QR code." },
    ],
  },
};

function App() {
  const checkAuth = useAuthStore((state) => state.checkAuth);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <BrowserRouter>
      <NuqsAdapter>
        <ShellProvider config={SHELL_CONFIG}>
          <GrootUIProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="customers" element={<Customers />} />
                <Route path="customers/:id" element={<CustomerDetail />} />
                <Route path="products" element={<Products />} />
                <Route path="orders" element={<Orders />} />
                <Route path="orders/:id" element={<OrderDetail />} />
                <Route path="photo-requests" element={<PhotoRequests />} />
                <Route path="photo-requests/:id" element={<PhotoRequestDetail />} />
                <Route path="moment-requests" element={<MomentRequests />} />
                <Route path="moment-requests/:id" element={<MomentRequestDetail />} />
                <Route path="print-batches" element={<PrintBatches />} />
                <Route path="storage" element={<Storage />} />
                <Route path="jobs" element={<Jobs />} />
                <Route path="jobs/:queueName/:jobId" element={<JobDetail />} />
                <Route path="settings" element={<Settings />} />
              </Route>
              <Route
                path="/orders/:id/invoice"
                element={
                  <ProtectedRoute>
                    <OrderInvoice />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/print-batches/:id"
                element={
                  <ProtectedRoute>
                    <Suspense
                      fallback={
                        <LoadingState text="Opening the print canvas…" className="h-screen" />
                      }
                    >
                      <PrintBatchEditor />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/public/print-previews/:previewLink"
                element={
                  <Suspense fallback={<div className="min-h-dvh bg-[#F2F1EC]" />}>
                    <PublicPrintPreview />
                  </Suspense>
                }
              />
              <Route path="/public/photo-requests/:publicLink" element={<PublicPhotoUpload />} />
              <Route path="/public/moment-requests/:publicLink" element={<PublicMomentUpload />} />
              <Route path="/public/moment-requests/view/:viewLink" element={<PublicMomentView />} />
              <Route path="/public/orders/:publicLink" element={<PublicOrderDetail />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </GrootUIProvider>
        </ShellProvider>
      </NuqsAdapter>
    </BrowserRouter>
  );
}

export default App;
